/*
 * NFT Landing Page - Sailing Ships Collection
 * Copyright (C) 2025 jsaif
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 *
 * Original project: https://github.com/s-shemmee/NFT-Portfolio-Landing-Page
 * Original creator: s-shemmee
 *
 * Cloud Animation Background:
 * Based on CodePen by Shaw
 * Source: https://codepen.io/shshaw/pen/jOMdaL
 * License: CodePen License (typically MIT/Open Source) - Used with attribution
 */

import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import "./About.css";
import collectionImage from "../images/collection.png";

export default function About() {
  const [modalImg, setModalImg] = useState(null);

  const openModal = (src, alt) => {
    setModalImg({ src, alt });
  };

  const closeModal = () => {
    setModalImg(null);
  };

  // Full scroll locking behavior + Escape key + cleanup
  useEffect(() => {
    if (modalImg) {
      // Store original overflow values
      const originalDocumentOverflow = document.documentElement.style.overflow;
      const originalBodyOverflow = document.body.style.overflow;

      // Apply overflow hidden to prevent scrolling without position fixed
      document.documentElement.style.overflow = "hidden";
      document.body.style.overflow = "hidden";
      document.body.classList.add("modal-open");

      const handleEscape = (e) => {
        if (e.key === "Escape") {
          closeModal();
        }
      };
      document.addEventListener("keydown", handleEscape);

      return () => {
        document.body.classList.remove("modal-open");
        // Restore original overflow values
        document.documentElement.style.overflow = originalDocumentOverflow;
        document.body.style.overflow = originalBodyOverflow;
        document.removeEventListener("keydown", handleEscape);
      };
    }
  }, [modalImg]);

  return (
    <section className="about" id="about">
      <div className="container-fluid">
        <h3 className="about-title">
          <span>ABOUT COLLECTION</span>
        </h3>
        <div className="row justify-content-center">
          <div className="col-md-10 panel-content">
            <div className="Clouds">
              <div className="Cloud Foreground"></div>
              <div className="Cloud Background"></div>
              <div className="Cloud Foreground"></div>
              <div className="Cloud Background"></div>
              <div className="Cloud Foreground"></div>
              <div className="Cloud Background"></div>
              <div className="Cloud Foreground"></div>
              <div className="Cloud Background"></div>
              <div className="Cloud Foreground"></div>
              <div className="Cloud Background"></div>
            </div>
            <div className="row">
              <div className="col-md-5">
                <button
                  onClick={() => openModal(collectionImage, "Collection")}
                  style={{
                    border: "none",
                    background: "transparent",
                    padding: 0,
                    cursor: "pointer",
                    display: "block",
                  }}
                  title="View collection image"
                  aria-label="View collection image in fullscreen"
                >
                  <img src={collectionImage} className="img-fluid about-content-image" alt="Collection" />
                </button>
              </div>
              <div className="col-md-7">
                <p className="about-content">
                  "Sailing Through Time & Dreams" is a one-of-a-kind NFT collection where each ship tells a story,
                  crafted from gemstones, born of myth, and powered by imagination.
                </p>
                <p className="about-content">
                  Leveraging AI-driven content, XRPL minting, and immersive lore, this collection is designed for deep
                  community engagement and lasting digital legacy. From storytelling videos to airdropped animated NFTs,
                  we connect art with narrative across social platforms.
                </p>
              </div>
            </div>

            {/* Stats Cards Row */}
            <div className="row justify-content-around mt-4">
              <Card
                title="Genesis Mint"
                value={
                  <a
                    href="https://xrp.cafe/collection/sailing-ships"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      color: "#9351f7",
                      textDecoration: "underline",
                      fontWeight: 600,
                    }}
                  >
                    Live Now
                  </a>
                }
              />
              <Card title="Ships Minted" value="50+" />
              <Card title="Gem-Based Ships" value="30+ Variants" />
              <Card title="1/1 Animated NFTs" value="Coming Soon" />
            </div>
          </div>
        </div>

        {/* Bottom Stats Section */}
        <div className="row justify-content-center mt-5">
          <div className="col-md-3">
            <div className="box">
              <i className="bi bi-layers"></i>
              <h2>2000+</h2>
              <p>Collection</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="box">
              <i className="bi bi-person-circle"></i>
              <h2>Jubi</h2>
              <p>Artist</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="box">
              <i className="bi bi-person-hearts"></i>
              <h2>450+</h2>
              <p>Community members</p>
            </div>
          </div>
          <div className="col-md-3">
            <div className="box">
              <i className="bi bi-rocket-takeoff-fill"></i>
              <h2>2.5x</h2>
              <p>Estimated value</p>
            </div>
          </div>
        </div>
      </div>

      {modalImg && (
        <div className="image-modal">
          <button
            className="modal-backdrop-button"
            onClick={closeModal}
            onKeyDown={(e) => {
              if (e.key === "Escape") {
                closeModal();
              }
            }}
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "transparent",
              border: "none",
              cursor: "default",
              zIndex: -1,
            }}
            aria-label="Close modal"
            tabIndex={-1}
          />
          <div className="image-modal-content">
            <div className="image-container">
              <img src={modalImg.src} alt={modalImg.alt} />
              <button className="close-modal-btn" onClick={closeModal} title="Close" aria-label="Close image modal">
                ✕
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

const Card = ({ title, value }) => {
  return (
    <div className="col-md-3 about-card">
      <h2>{value}</h2>
      <p>{title}</p>
    </div>
  );
};

Card.propTypes = {
  title: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
};
